import ErrorBoundary from "components/ErrorBoundary";
import Footer from "components/Footer";
import Header from "components/Header";
import routes from "config/routes";
import React, { lazy, Suspense } from "react";
import Loader from "react-loader-spinner";
import { Route, Routes } from "react-router-dom";

import "./App.css";

const lazyWithRetry = (componentImport) =>
  lazy(() => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem("page-has-been-force-refreshed") || "false"
    );
    return componentImport().then((component) => {
      window.localStorage.setItem("page-has-been-force-refreshed", "false");
      return component;
    }).catch((error) => {
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.localStorage.setItem("page-has-been-force-refreshed", "true");
        window.location.reload();
        // Return a never-resolving promise so React doesn't try to render undefined
        return new Promise(() => {});
      }
      throw error;
    });
  });

const ButtonToTopWrapper = lazyWithRetry(() => import("components/ButtonToTopWrapper"));
const Home = lazyWithRetry(() => import("pages/Home"));
const AboutUs = lazyWithRetry(() => import("pages/AboutUs"));
const Socialize = lazyWithRetry(() => import("pages/Socialize"));
const Health = lazyWithRetry(() => import("pages/Health"));
const Materials = lazyWithRetry(() => import("pages/Materials"));
const Survey = lazyWithRetry(() => import("pages/Survey"));
const Login = lazyWithRetry(() => import("pages/Login"));
const News = lazyWithRetry(() => import("pages/News"));
const Register = lazyWithRetry(() => import("pages/Register"));
const ConfirmEmail = lazyWithRetry(() => import("pages/Confirm"));
const ForgotPassword = lazyWithRetry(() => import("pages/ForgotPassword"));
const ChangePassword = lazyWithRetry(() => import("pages/ChangePassword"));
const ResetPassword = lazyWithRetry(() => import("pages/ResetPassword"));
const Article = lazyWithRetry(() => import("pages/CertainNews"));
const UserProfile = lazyWithRetry(() => import("pages/UserProfile"));
const NotFound = lazyWithRetry(() => import("pages/NotFound"));
const PostById = lazyWithRetry(() => import("pages/PostById"));
const AdminModeration = lazyWithRetry(() => import("pages/AdminModeration"));
const AdminSchools = lazyWithRetry(() => import("pages/AdminSchools"));
const ModeratorPosts = lazyWithRetry(() => import("pages/ModeratorPosts"));
const ModeratorSchools = lazyWithRetry(() => import("pages/ModeratorSchools"));
const AddSchool = lazyWithRetry(() => import("pages/AddSchool"));
const AllSchoolModeration = lazyWithRetry(() => import("pages/AllSchoolModeration"));
const AssessmentToolStatistics = lazyWithRetry(() =>
  import("pages/AssessmentToolStatistics"),
);
const AssessmentToolStatisticsBySchool = lazyWithRetry(() =>
  import("pages/AssessmentToolStatisticsBySchool"),
);
const UsersTable = lazyWithRetry(() => import("pages/UsersTable"));

function App() {
  return (
    <div className="mainContainer">
      <Header />
      <ErrorBoundary>
        <Suspense
        fallback={
          <div className="lazyLoaderWrapper">
            <Loader type="Bars" color="#ff8614" height={200} width={200} />
          </div>
        }
      >
        <Routes>
          <Route
            path={routes.home}
            exact
            element={
              <ButtonToTopWrapper>
                <Home />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.news}
            exact
            element={
              <ButtonToTopWrapper>
                <News />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.certainNews}
            element={
              <ButtonToTopWrapper>
                <Article />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.aboutUs}
            exact
            element={
              <ButtonToTopWrapper>
                <AboutUs />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.socialize}
            exact
            element={
              <ButtonToTopWrapper>
                <Socialize />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.postById}
            element={
              <ButtonToTopWrapper>
                <PostById />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.healthTopics}
            exact
            element={
              <ButtonToTopWrapper>
                <Health />
              </ButtonToTopWrapper>
            }
          />
          <Route
            exact
            path={routes.materialsAndInstructions}
            element={
              <ButtonToTopWrapper>
                <Materials />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.SHEsurvey}
            exact
            element={
              <ButtonToTopWrapper>
                <Survey />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.login}
            exact
            element={
              <ButtonToTopWrapper>
                <Login />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.register}
            exact
            element={
              <ButtonToTopWrapper>
                <Register />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.userProfile}
            exact
            element={
              <ButtonToTopWrapper>
                <UserProfile />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.confirmEmail}
            exact
            element={
              <ButtonToTopWrapper>
                <ConfirmEmail />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.forgotPassword}
            exact
            element={
              <ButtonToTopWrapper>
                <ForgotPassword />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.resetPassword}
            exact
            element={
              <ButtonToTopWrapper>
                <ResetPassword />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.changePassword}
            exact
            element={
              <ButtonToTopWrapper>
                <ChangePassword />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.adminUsers}
            exact
            element={
              <ButtonToTopWrapper>
                <UsersTable />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.adminModeration}
            exact
            element={
              <ButtonToTopWrapper>
                <AdminModeration />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.specificModeration}
            exact
            element={
              <ButtonToTopWrapper>
                <AllSchoolModeration />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.adminSchools}
            exact
            element={
              <ButtonToTopWrapper>
                <AdminSchools />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.moderatorUsers}
            exact
            element={
              <ButtonToTopWrapper>
                <UsersTable />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.moderatorSchoolUsers}
            exact
            element={
              <ButtonToTopWrapper>
                <UsersTable />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.adminSchoolUsers}
            exact
            element={
              <ButtonToTopWrapper>
                <UsersTable />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.moderatorModeration}
            exact
            element={
              <ButtonToTopWrapper>
                <ModeratorPosts />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.moderatorSchools}
            exact
            element={
              <ButtonToTopWrapper>
                <ModeratorSchools />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.adminAddSchool}
            exact
            element={
              <ButtonToTopWrapper>
                <AddSchool />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.assessmentToolStatistics}
            exact
            element={
              <ButtonToTopWrapper>
                <AssessmentToolStatistics />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.assessmentToolStatisticsBySchool}
            exact
            element={
              <ButtonToTopWrapper>
                <AssessmentToolStatisticsBySchool />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path={routes.notFound}
            element={
              <ButtonToTopWrapper>
                <NotFound />
              </ButtonToTopWrapper>
            }
          />
          <Route
            path="*"
            element={
              <ButtonToTopWrapper>
                <NotFound />
              </ButtonToTopWrapper>
            }
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
      <Footer />
    </div>
  );
}

export default App;
